import React from "react";

import { GoalBasedFinancialPlanSingleResponse } from "@fartherfinance/frontend/api/FinancialPlans/plan/types";

import { actions } from "../../reducer/actions";
import { useCreateUpdateFinancialPlanContext } from "../../reducer/Context";
import CardsRow from "../Shared/CardsRow/CardsRow";
import OverviewCard from "../Shared/OverviewCard/OverviewCard";
import BodyContentFormatter from "@src/sharedComponents/BodyContentFormatter/BodyContentFormatter";
import Text from "@src/sharedComponents/Typography/Text";

import styles from "./Overview.module.css";

interface Props {
  plan: GoalBasedFinancialPlanSingleResponse;
}

const Overview: React.FC<Props> = ({ plan }) => {
  const hasGoals = plan.goals.length > 0;
  const hasAssets = plan.accounts.length > 0;
  const hasIncome = plan.income.length > 0;
  const hasSavings = plan.savings.length > 0;
  const hasExpenses = plan.expenses.length > 0;
  const hasInsurance = plan.insurance.length > 0;

  const { dispatch } = useCreateUpdateFinancialPlanContext();

  const handleCardClick = (idx: number) => {
    dispatch({ type: actions.SET_PAGE_INDEX, payload: idx });
  };

  return (
    <BodyContentFormatter>
      <div className={styles.centering}>
        <Text variant={"heading1"} color={"text"} className={styles.heading}>
          Overview of Steps
        </Text>

        <CardsRow maxColumns={3}>
          <OverviewCard
            index={1}
            title={"Goals"}
            text={
              "Add goals like retirement, vacations, and college savings for your kids."
            }
            isClickable={true}
            onClick={() => handleCardClick(1)}
          />

          <OverviewCard
            index={2}
            title={"Assets"}
            text={"Choose which accounts to add to your plan."}
            isClickable={hasGoals || hasAssets}
            onClick={() => handleCardClick(2)}
          />

          <OverviewCard
            index={3}
            title={"Income"}
            text={"Capture all the income you earn and expect to earn."}
            isClickable={hasAssets || hasIncome}
            onClick={() => handleCardClick(3)}
          />
        </CardsRow>

        <CardsRow maxColumns={3}>
          <OverviewCard
            index={4}
            title={"Savings"}
            text={"Track how much you plan to save each year."}
            isClickable={hasIncome || hasSavings}
            onClick={() => handleCardClick(4)}
          />

          <OverviewCard
            index={5}
            title={"Expenses"}
            text={"Plan for expenses before and during retirement."}
            isClickable={hasSavings || hasExpenses}
            onClick={() => handleCardClick(5)}
          />

          <OverviewCard
            index={6}
            title={"Insurance"}
            text={"Add details about your current insurance coverage."}
            isClickable={hasExpenses || hasInsurance}
            onClick={() => handleCardClick(6)}
          />
        </CardsRow>
      </div>
    </BodyContentFormatter>
  );
};

export default Overview;
